import React from 'react'
import { LucideIcon } from 'lucide-react'
import AskAgentButton from './AskAgentButton'

// Ask Agent context for connecting KPI to agents
interface AskAgentContext {
  metric: string
  value?: number | string
  delta?: number
  week?: string
  retailerId?: string
}

interface HeroKpiCardProps {
  label: string
  value: string | number
  icon: LucideIcon
  yoyDeltaText?: string
  yoyDeltaTone?: 'success' | 'danger' | 'neutral'
  wowDeltaText?: string
  wowDeltaTone?: 'success' | 'danger' | 'neutral'
  onClick?: () => void
  className?: string
  /** Enable Ask Agent button with context */
  askAgent?: AskAgentContext
}

export function HeroKpiCard({ 
  label, 
  value, 
  icon: Icon, 
  yoyDeltaText, 
  yoyDeltaTone = 'neutral',
  wowDeltaText,
  wowDeltaTone = 'neutral',
  onClick,
  className = '',
  askAgent
}: HeroKpiCardProps) {
  
  const isClickable = !!onClick
  
  const getToneClasses = (tone: 'success' | 'danger' | 'neutral') => {
    switch (tone) {
      case 'success':
        return 'bg-green-50 text-green-700 border-green-200'
      case 'danger':
        return 'bg-red-50 text-red-700 border-red-200'
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200'
    }
  }

  const cardClasses = `
    kpi-card
    ${isClickable ? 'cursor-pointer' : ''}
    ${className}
  `.trim()

  return (
    <div 
      className={cardClasses}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="relative">
        {/* Icon + Ask Agent Button */}
        <div className="flex justify-end mb-1 gap-1">
          {askAgent && (
            <AskAgentButton
              metric={askAgent.metric}
              value={askAgent.value}
              delta={askAgent.delta}
              week={askAgent.week}
              retailerId={askAgent.retailerId}
              variant="icon"
              size="sm"
              tooltip={`Ask AI about ${label}`}
            />
          )}
          <div className="p-1.5 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-lg shadow-md shadow-emerald-500/30">
            <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
        </div>

        {/* Primary Value - Compact */}
        <div className="mb-1">
          <p 
            className="text-xl font-bold text-gray-900 tracking-tight kpi-number"
            aria-label={`${label}: ${value}`}
          >
            {value}
          </p>
        </div>

        {/* Label */}
        <div className="mb-1">
          <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">{label}</p>
        </div>

        {/* YoY Delta Chip */}
        {(yoyDeltaText || wowDeltaText) && (
          <div className="flex flex-wrap gap-1">
            {yoyDeltaText && (
              <div className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${getToneClasses(yoyDeltaTone)}`}>
                {yoyDeltaText}
              </div>
            )}
            {wowDeltaText && (
              <div className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${getToneClasses(wowDeltaTone)}`}>
                {wowDeltaText}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default HeroKpiCard
